import { useQueries, useQuery } from '@tanstack/react-query'
import { fetchApiWithToken, getBatchFetcher } from '~/api/utils'
import type { ContactHeadResponse } from '../types'

const contactHeadBatcher = <T extends ContactHeadResponse>(
  additionalFields: string[]
) =>
  getBatchFetcher<T>(
    `contactHead_${additionalFields.join('_')}`,
    async (ids) => {
      return await fetchApiWithToken<T[]>({
        method: 'get',
        url: `/v1/contacts?id[]=${ids.join('&id[]=')}&itemsPerPage=1000&fields[]=id&fields[]=firstname&fields[]=lastname&fields[]=civility&fields[]=acceptsMessaging${additionalFields ? `&fields[]=${additionalFields.join('&fields[]=')}` : ''}`
      })
    }
  )

export function useContactsHead<T extends ContactHeadResponse>({
  contactIds,
  additionalFields
}: {
  contactIds: number[]
  additionalFields?: string[]
}) {
  return useQueries({
    queries: contactIds.map((contactId) => ({
      queryKey: ['contacts', 'head', contactId, additionalFields],
      queryFn: () =>
        contactHeadBatcher<T>(additionalFields ?? []).fetch(
          contactId as number
        ),
      staleTime: 300_000
    }))
  })
}

export function useContactHead<T extends ContactHeadResponse>({
  contactId,
  additionalFields
}: {
  contactId?: number
  additionalFields?: string[]
}) {
  return useQuery({
    queryKey: ['contacts', 'head', contactId, additionalFields],
    queryFn: () =>
      contactHeadBatcher<T>(additionalFields ?? []).fetch(contactId as number),
    enabled: !!contactId,
    staleTime: 300_000
  })
}
